import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Button,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Divider,
  Tooltip,
} from '@mui/material';
import { CheckIcon, ChevronRightIcon } from '@atlas/ui';
import type { DivisionId } from '@atlas/shared';
import { useDivision } from '../../contexts/DivisionContext';

/**
 * Top-bar division (company brand) switcher. Shows the current division's logo;
 * opening the menu lets the user switch inline or jump to the full launch
 * screen. Renders nothing until a division has been chosen (the launch-screen
 * gate handles the no-division case).
 */
export function DivisionSwitcher() {
  const navigate = useNavigate();
  const { division, currentDivision, availableDivisions, setDivision } = useDivision();
  const [anchor, setAnchor] = useState<null | HTMLElement>(null);
  const open = Boolean(anchor);

  // Nothing to switch when the user reaches zero or one division.
  if (!currentDivision || availableDivisions.length <= 1) return null;

  const handlePick = (id: DivisionId) => {
    setAnchor(null);
    if (id !== division) setDivision(id);
  };

  return (
    <>
      <Tooltip title={`Division: ${currentDivision.label} — switch`}>
        <Button
          onClick={(e) => setAnchor(e.currentTarget)}
          aria-label={`Current division: ${currentDivision.label}. Switch division.`}
          aria-haspopup="menu"
          aria-expanded={open}
          sx={{
            px: 1,
            py: 0.5,
            minWidth: 0,
            borderRadius: 1,
            border: 1,
            borderColor: 'divider',
          }}
        >
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              bgcolor: '#fff',
              borderRadius: 0.75,
              px: 0.75,
              height: 26,
            }}
          >
            <Box
              component="img"
              src={currentDivision.logo}
              alt={currentDivision.label}
              sx={{ height: 18, maxWidth: 96, objectFit: 'contain', display: 'block' }}
            />
          </Box>
        </Button>
      </Tooltip>

      <Menu
        anchorEl={anchor}
        open={open}
        onClose={() => setAnchor(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
        slotProps={{ paper: { sx: { minWidth: 240, mt: 0.5 } } }}
      >
        {availableDivisions.map((d) => (
          <MenuItem key={d.id} selected={d.id === division} onClick={() => handlePick(d.id)}>
            <ListItemIcon>
              {d.id === division ? <CheckIcon fontSize="small" /> : <Box sx={{ width: 20 }} />}
            </ListItemIcon>
            <ListItemText>{d.label}</ListItemText>
          </MenuItem>
        ))}
        <Divider />
        <MenuItem
          onClick={() => {
            setAnchor(null);
            navigate('/select-division');
          }}
        >
          <ListItemIcon>
            <ChevronRightIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>Open launch screen</ListItemText>
        </MenuItem>
      </Menu>
    </>
  );
}
